import { FC, Fragment, useState } from 'react';
// Hooks
import useToggle from 'hooks/useToggle';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch } from 'hooks/useStore';
// Types
import QuizEventStatuses from 'types/QuizEventStatuses';
// Models
import IQuizEvent from 'models/QuizEvent';
// Async
import { deleteQuizEvent, triggerQuizEvent } from 'store/quizEvent/quizEventAsync';
// Components
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import QuizEventDialog from 'dialogs/QuizEvent.dialog';
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
// Icons
import { TbHandClick } from "react-icons/tb";
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
// Utilities
import { textFromCamelToNormalCase } from 'utilities/utilities';

type Props = {
  quizEvent: IQuizEvent;
  quizId: string;
}

const QuizEventListItem:FC<Props> = ({ quizEvent, quizId }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  const trigger = () => {
    dispatch(triggerQuizEvent(quizEvent._id));
  }

  const handleDelete = () => {
    setLoading(true);
    dispatch(deleteQuizEvent(quizEvent._id)).unwrap()
      .finally(() => setLoading(false));
  }

  const { open, toggle } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  return (
    <Fragment>
      <tr>
        <td className="text-left title">{quizEvent.name}</td>
        <td className="text-left text">{textFromCamelToNormalCase(quizEvent.type)}</td>
        <td className="text-left text">{textFromCamelToNormalCase(quizEvent.status)}</td>
        <td className="text-left text">{quizEvent.triggeredBy ? `${quizEvent.triggeredBy.name} ${quizEvent.triggeredBy.surname}` : ''}</td>
        <td className="text-right actions">
          {quizEvent.status !== QuizEventStatuses.Triggered ? [
            <Tooltip key="trigger-quiz-event" title={t('pages.quizEvents.triggerButton')}>
              <IconButton
                onClick={trigger}
              ><TbHandClick /></IconButton>
            </Tooltip>,
            <Tooltip key="edit-quiz-event" title={t('buttons.edit')}>
              <IconButton
                onClick={toggle}
              ><FaEdit /></IconButton>
            </Tooltip>
          ] : null}
          <Tooltip title={t('buttons.delete')}>
            <IconButton
              onClick={toggleConfirmationDialog}
            ><FaTrashAlt /></IconButton>
          </Tooltip>
        </td>
      </tr>

      {open ? (
        <QuizEventDialog open={open} onClose={toggle} quizId={quizId} quizEvent={quizEvent} />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          onConfirm={handleDelete}
          title={t('dialogs.quizEvent.deleteTitle', { quizEventName: quizEvent.name })}
          content={t('dialogs.quizEvent.deleteConfirmation')}
          loading={loading}
        />
      ) : null}
    </Fragment>
  )
}

export default QuizEventListItem;
